import React, { useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { InfoText } from './InfoText';
import { CustomSelect } from './CustomSelect';

export function getErrorMessage(fieldName, formik) {
    if (!fieldName || !formik || !formik.errors) return null;
    if (formik.errors[fieldName]) return formik.errors[fieldName];
    return null;
}

export function renderErrors(errors) {
    if (Array.isArray(errors))
        return errors.map((error, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={i}>
                {error}
                {i < errors.length - 1 && <br />}
            </React.Fragment>
        ));
    return errors || null;
}

export function fieldTypeAdapter(type) {
    if (['rustext', 'vkUrl', 'time'].includes(type)) return 'text';
    return type;
}

export function getControlComponentAndPropsForField(field, formik, otherProps) {
    const { id: fieldId, code, name: label, inputType: type, description: helpText, required } = field;
    const name = code || fieldId;
    let controlProps = otherProps || {};
    let ControlComponent = Form.Control;
    let showLabel = !!label;
    let labelText = label;
    if (required) labelText += '*';

    controlProps.id = `control-${name}`;
    controlProps.name = name;
    controlProps.required = required;
    controlProps.size = controlProps.size || 'sm';

    if (formik) {
        controlProps.value = formik.values[name];
        controlProps.onBlur = formik.handleBlur;
    }
    controlProps.value = controlProps.value || '';

    if (type === 'date') {
        controlProps.as = DatePicker;
        controlProps.isClearable = true;
        controlProps.locale = 'ru';
        controlProps.selected = controlProps.value ? new Date(controlProps.value) : null;
        controlProps.dateFormat = 'dd.MM.yyyy';
        controlProps.placeholderText = 'ДД.ММ.ГГГГ';
        delete controlProps.value;
    } else if (type === 'checkbox') {
        controlProps.type = type;
        controlProps.label = labelText;
        showLabel = false;
        ControlComponent = Form.Check;
        if (controlProps.size === 'sm') controlProps.className = 'small';
    } else if (type === 'textarea') {
        controlProps.as = 'textarea';
    } else if (type === 'sex') {
        controlProps.as = 'select';
        controlProps.children = (
            <>
                <option value="" className="font-italic">
                    Не выбрано...
                </option>
                <option value="MALE">Мальчик</option>
                <option value="FEMALE">Девочка</option>
            </>
        );
    } else if (type === 'role') {
        controlProps.as = 'select';
        controlProps.children = (
            <>
                <option value="" className="font-italic">
                    Не выбрано...
                </option>
                <option value="participant">Участник</option>
                <option value="assistant">Помощник</option>
            </>
        );
    } else if (type === 'infotext') {
        ControlComponent = InfoText;
        controlProps = {
            label: labelText,
            description: helpText,
        };
        showLabel = false;
    } else if (type === 'fonostart') {
        ControlComponent = CustomSelect;
        controlProps.items = ['сразу после объявления выхода', 'запускать с точки'];
    } else if (type === 'repaday') {
        controlProps.as = 'select';
        controlProps.children = (
            <>
                <option value="" className="font-italic">
                    Не выбрано...
                </option>
                <option value="В пятницу">В пятницу</option>
                <option value="В субботу">В субботу</option>
                <option value="В воскресение">В воскресение</option>
                <option value="В любой день">В любой день</option>
                <option value="Репетиция не нужна">Репетиция не нужна</option>
            </>
        );
    } else if (type === 'programblock') {
        controlProps.as = 'select';
        controlProps.children = (
            <>
                <option value="" className="font-italic">
                    Не выбрано...
                </option>
                <option value="Не важно">Не важно</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
            </>
        );
    } else {
        controlProps.type = fieldTypeAdapter(type);
    }

    return [ControlComponent, controlProps, name, type, labelText, showLabel, helpText];
}

export function useEscCallback(onEscape) {
    const escFunction = useCallback(
        (event) => {
            if (event.keyCode === 27) {
                onEscape(event);
            }
        },
        [onEscape]
    );
    useEffect(() => {
        document.addEventListener('keydown', escFunction, false);
        return () => {
            document.removeEventListener('keydown', escFunction, false);
        };
    }, [escFunction]);
}

export function useErrors(name, formik, outErrors) {
    const { errors } = formik || {};
    return useMemo(() => {
        if (!outErrors && !formik) return null;
        if (outErrors)
            if (typeof outErrors === 'object' && !Array.isArray(outErrors)) {
                if (outErrors[name]) return renderErrors(outErrors[name]);
            } else return renderErrors(outErrors);
        return renderErrors(getErrorMessage(name, formik));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, formik, outErrors, errors]);
}
